import SquareImageBox from "views/components/CardBodyBoxes/SquareImageBox";
import StripeImageBox from "views/components/CardBodyBoxes/StripeImageBox";
import ImagePreview from "views/pages/EnrichmentPage/ImagePreview";
import ImageBox from "views/components/CardBodyBoxes/ImageBox";

import { CardBodyImage, CardTypes, OnUpdateAssetData } from "types";
import classNames from "classnames";
import { useEffect } from "react";

import styles from "./style.module.scss";

export interface CardBodyProps {
  bodyEntityType: CardTypes;
  mdLabels: string[];
  hoverDelay: number;
  isHovering: boolean;
  videoURIs: string[];
  unClickable: boolean;
  isSingleObject: boolean;
  boundingBox: any[] | null;
  plainImage: CardBodyImage;
  thumbImage: CardBodyImage;
  videoURIForCluster: string;
  plainImages: CardBodyImage[];
  thumbImages: CardBodyImage[];
  isAssetPreviewEnabled: boolean;
  onEndContentLoading: () => void;
  onUpdateAssetData: OnUpdateAssetData;
  opacity?: number;
}

const CardBody = ({
  plainImages,
  thumbImages,
  isHovering,
  bodyEntityType,
  plainImage,
  thumbImage,
  unClickable,
  boundingBox,
  isSingleObject,
  videoURIForCluster,
  videoURIs,
  isAssetPreviewEnabled,
  mdLabels,
  hoverDelay,
  onUpdateAssetData,
  onEndContentLoading,
  opacity
}: CardBodyProps) => {
  const cardBodyClassnames = classNames(styles.cardBody, {
    [styles.hoveredCardBody]: isHovering,
    [styles.cardBodyNotClickable]: unClickable,
  });

  useEffect(() => {
    if (isSingleObject) {
      onEndContentLoading();
    }
  }, [isSingleObject, onEndContentLoading]);

  // if single image, render a single image
  const renderSquareImages = () => {
    return (
      <SquareImageBox
        videoURIs={videoURIs}
        hoverDelay={hoverDelay}
        plainImages={plainImages}
        thumbImages={thumbImages}
        showAssetPreview={isAssetPreviewEnabled}
        onUpdateAssetData={onUpdateAssetData}
        onEndContentLoading={onEndContentLoading}
      />
    );
  };

  // if count is less thatn four and more than one render stripe images
  const renderStripeImages = () => {
    return (
      <StripeImageBox
        videoURIs={videoURIs}
        hoverDelay={hoverDelay}
        plainImages={plainImages}
        thumbImages={thumbImages}
        showAssetPreview={isAssetPreviewEnabled}
        onUpdateAssetData={onUpdateAssetData}
        onEndContentLoading={onEndContentLoading}
      />
    );
  };

  // if count is more than  four render square images
  const renderSingleImage = () => {
    const boundingBoxes = boundingBox || [];
    const thumbSrc = thumbImages[0]?.src || thumbImage?.src || "";
    const src = plainImages[0]?.src || plainImage?.src || "";
    const relevanceType = plainImage?.relevanceType || null;

    const dynamicUpdate = () => {
      onUpdateAssetData(
        plainImage.id || plainImages[0].id,
        plainImage.src || plainImages[0].src,
      );
    };

    return isSingleObject ? (
      <ImagePreview
        src={src}
        labels={mdLabels}
        hoverDelay={hoverDelay}
        boundingBoxes={boundingBoxes}
        relevanceType={relevanceType}
        showAssetPreview={isAssetPreviewEnabled}
        onUpdateAssetData={dynamicUpdate}
      />
    ) : (
      <ImageBox
        image={src}
        imageThumb={thumbSrc}
        hoverDelay={hoverDelay}
        boundingBox={boundingBox}
        isObject={isSingleObject}
        relevanceType={relevanceType}
        showVideoIcon={!!videoURIForCluster}
        showAssetPreview={isAssetPreviewEnabled}
        onUpdateAssetData={dynamicUpdate}
        onEndContentLoading={onEndContentLoading}
      />
    );
  };

  const renderCardBody = () => {
    if (plainImages.length > 3) {
      return renderSquareImages();
    } else if (plainImages.length > 1) {
      return renderStripeImages();
    } else if (plainImages.length === 1 || !!plainImage.src) {
      return renderSingleImage();
    } else {
      return <noscript />;
    }
  };

  return <div className={cardBodyClassnames}
              style={{opacity: opacity ? opacity + "%" : "100%"}}>{renderCardBody()}</div>;
};

export default CardBody;
